/**
 * Feedback / Audit 학습 대시보드.
 * hs_classify_audit 누적 데이터 시각화 — 자가학습 가시화.
 *
 * URL: /?audit=feedback-dashboard
 *
 * Endpoints:
 *  - /api/onto/audit-summary  (통계)
 *  - /api/onto/audit-history  (최근 분류 + 정정 이력)
 */
import React, { useState, useEffect } from "react";
import { ArrowLeft, TrendingUp, CheckCircle2, AlertCircle } from "lucide-react";
import AuditSideNav from "./AuditSideNav";
import { isLoginHSProduct } from "../config/product";
import "./theme.css";

// 도메인별 브랜드(loginhs.* → LoginHS).
const BRAND = isLoginHSProduct() ? "LoginHS" : "DocuAI";

function Metric({ label, value, unit, accent = "var(--audit-accent)" }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 130,
        background: "var(--audit-bg)",
        border: "1px solid var(--audit-border)",
        borderRadius: 8,
        padding: 12,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--audit-text-muted)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent }}>
        {value}
        {unit && (
          <span
            style={{
              fontSize: 12,
              marginLeft: 2,
              color: "var(--audit-text-muted)",
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function Card({ title, children, accent = "#1a73e8" }) {
  return (
    <div
      style={{
        background: "var(--audit-bg-elevated)",
        border: "1px solid var(--audit-border)",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        padding: "16px 20px",
        marginBottom: 14,
      }}
    >
      <h3
        style={{
          fontSize: 13,
          margin: "0 0 10px",
          fontWeight: 700,
          color: accent,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

const TBL_STYLE = {
  width: "100%",
  fontSize: 12,
  borderCollapse: "collapse",
};
const TH = {
  textAlign: "left",
  padding: "5px 8px",
  borderBottom: "1px solid var(--audit-border)",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  background: "var(--audit-bg)",
};
const TD = {
  padding: "5px 8px",
  borderTop: "1px solid var(--audit-border)",
  verticalAlign: "top",
};

function MiniBar({ value, max, accent = "var(--audit-accent)" }) {
  const w = max > 0 ? Math.max((value / max) * 100, 2) : 0;
  return (
    <div
      style={{
        width: 140,
        height: 8,
        background: "var(--audit-bg)",
        border: "1px solid var(--audit-border)",
        borderRadius: 2,
        position: "relative",
      }}
    >
      <div
        style={{
          width: `${w}%`,
          height: "100%",
          background: accent,
        }}
      />
    </div>
  );
}

export default function FeedbackDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("/api/onto/audit-summary").then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`summary HTTP ${r.status}`)),
      ),
      fetch("/api/onto/audit-history?limit=100").then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`history HTTP ${r.status}`)),
      ),
    ])
      .then(([s, h]) => {
        setSummary(s);
        setHistory(h);
      })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, []);

  const maxChapter = summary
    ? Math.max(...(summary.by_chapter || []).map((r) => r.n), 1)
    : 1;
  const maxDay = summary
    ? Math.max(...(summary.by_day || []).map((r) => r.n), 1)
    : 1;
  const correctionPct = summary
    ? (summary.correction_rate * 100).toFixed(2)
    : "0.00";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        background: "var(--audit-bg)",
        color: "var(--audit-text)",
      }}
    >
      <AuditSideNav />
      <div className="audit-root" style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid var(--audit-border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={() => (window.location.href = "/?audit=hs-hub")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "transparent",
              border: "1px solid var(--audit-border)",
              borderRadius: 4,
              padding: "3px 8px",
              cursor: "pointer",
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontFamily: "inherit",
            }}
          >
            <ArrowLeft size={12} /> Hub
          </button>
          <span style={{ color: "var(--audit-text-faint)", fontSize: 12 }}>
            /
          </span>
          <TrendingUp size={16} style={{ color: "var(--audit-accent)" }} />
          <h1 style={{ fontSize: 14, margin: 0, fontWeight: 600 }}>
            피드백 학습 대시보드
          </h1>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              marginLeft: 8,
            }}
          >
            누적 분류 + 사용자 정정 추세 + 자가학습 가시화
          </span>
        </div>

        <div style={{ padding: "20px 28px", maxWidth: 1100, margin: "0 auto" }}>
          {loading && (
            <div style={{ color: "var(--audit-text-muted)", fontSize: 13 }}>
              불러오는 중…
            </div>
          )}
          {err && (
            <div
              style={{
                color: "#d93025",
                fontSize: 12,
                padding: 10,
                border: "1px solid #d93025",
                borderRadius: 4,
              }}
            >
              오류: {err}
            </div>
          )}
          {summary && (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  marginBottom: 22,
                }}
              >
                <Metric
                  label="총 분류 요청"
                  value={summary.total}
                  accent="#1a73e8"
                />
                <Metric
                  label="정정된 분류 (사용자)"
                  value={summary.reviewed_corrections}
                  accent="#1a73e8"
                />
                <Metric
                  label="정정율"
                  value={correctionPct}
                  unit="%"
                  accent="#1a73e8"
                />
                <Metric
                  label="활성 chapter"
                  value={summary.by_chapter?.length || 0}
                  accent="#1a73e8"
                />
              </div>

              <Card title="최근 30일 일별 분류 요청" accent="#1a73e8">
                {(summary.by_day || []).length === 0 ? (
                  <div
                    style={{ color: "var(--audit-text-muted)", fontSize: 12 }}
                  >
                    아직 데이터 없음
                  </div>
                ) : (
                  <table style={TBL_STYLE}>
                    <thead>
                      <tr>
                        <th style={TH}>날짜</th>
                        <th style={TH}>요청</th>
                        <th style={TH}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_day.map((r) => (
                        <tr key={r.day}>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {String(r.day).slice(0, 10)}
                          </td>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.n}
                          </td>
                          <td style={TD}>
                            <MiniBar
                              value={r.n}
                              max={maxDay}
                              accent="#1a73e8"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <Card title="Chapter 별 분류 분포 (top 10)" accent="#1a73e8">
                {(summary.by_chapter || []).length === 0 ? (
                  <div
                    style={{ color: "var(--audit-text-muted)", fontSize: 12 }}
                  >
                    아직 데이터 없음
                  </div>
                ) : (
                  <table style={TBL_STYLE}>
                    <thead>
                      <tr>
                        <th style={TH}>HS 4자리</th>
                        <th style={TH}>요청</th>
                        <th style={TH}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_chapter.map((r) => (
                        <tr key={r.hs4}>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                              fontWeight: 600,
                            }}
                          >
                            {r.hs4}
                          </td>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.n}
                          </td>
                          <td style={TD}>
                            <MiniBar
                              value={r.n}
                              max={maxChapter}
                              accent="#1a73e8"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <Card title="신뢰도 분포" accent="#1a73e8">
                  <table style={TBL_STYLE}>
                    <thead>
                      <tr>
                        <th style={TH}>level</th>
                        <th style={TH}>count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.by_confidence || []).map((r) => (
                        <tr key={r.confidence}>
                          <td style={TD}>{r.confidence}</td>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.n}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
                <Card title="Pipeline 사용 분포" accent="#1a73e8">
                  <table style={TBL_STYLE}>
                    <thead>
                      <tr>
                        <th style={TH}>pipeline</th>
                        <th style={TH}>count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.by_pipeline || []).map((r) => (
                        <tr key={r.pipeline}>
                          <td style={TD}>{r.pipeline}</td>
                          <td
                            style={{
                              ...TD,
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.n}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </div>
            </>
          )}

          {history && (
            <Card title="최근 분류 히스토리 (100건)" accent="#1a73e8">
              <div style={{ overflowX: "auto" }}>
                <table style={TBL_STYLE}>
                  <thead>
                    <tr>
                      <th style={TH}>요청시간</th>
                      <th style={TH}>line_item</th>
                      <th style={TH}>예측 HSK</th>
                      <th style={TH}>conf</th>
                      <th style={TH}>정정</th>
                      <th style={TH}>turn</th>
                      <th style={TH}>response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.items.map((it) => (
                      <tr key={it.id}>
                        <td
                          style={{
                            ...TD,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 12,
                            color: "var(--audit-text-muted)",
                          }}
                        >
                          {String(it.requested_at)
                            .slice(0, 16)
                            .replace("T", " ")}
                        </td>
                        <td style={{ ...TD, maxWidth: 280 }}>
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={it.line_item}
                          >
                            {it.line_item}
                          </div>
                        </td>
                        <td
                          style={{
                            ...TD,
                            fontFamily: "JetBrains Mono, monospace",
                            fontWeight: 600,
                          }}
                        >
                          {it.predicted_hsk || it.predicted_hs4 || "—"}
                        </td>
                        <td
                          style={{
                            ...TD,
                            color:
                              it.confidence === "high"
                                ? "#0f9d58"
                                : it.confidence === "medium"
                                  ? "#f9a825"
                                  : "var(--audit-text-muted)",
                          }}
                        >
                          {it.confidence || "—"}
                        </td>
                        <td style={TD}>
                          {it.was_corrected ? (
                            <span
                              style={{
                                color: "#d93025",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                              title={`정정: ${it.reviewed_hsk}`}
                            >
                              <AlertCircle size={12} /> {it.reviewed_hsk}
                            </span>
                          ) : it.reviewed_hsk ? (
                            <span
                              style={{ color: "#0f9d58" }}
                              title="사용자 확인"
                            >
                              <CheckCircle2 size={12} />
                            </span>
                          ) : (
                            <span style={{ color: "var(--audit-text-faint)" }}>
                              —
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            ...TD,
                            fontFamily: "JetBrains Mono, monospace",
                            color: "var(--audit-text-muted)",
                          }}
                        >
                          {it.turn_count || 0}
                        </td>
                        <td
                          style={{
                            ...TD,
                            fontFamily: "JetBrains Mono, monospace",
                            color: "var(--audit-text-muted)",
                          }}
                        >
                          {it.response_time_ms
                            ? `${it.response_time_ms}ms`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div
            style={{
              marginTop: 20,
              padding: 14,
              background: "var(--audit-bg-elevated)",
              border: "1px solid var(--audit-border)",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--audit-text-muted)",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            {BRAND} 자가학습 — 사용자 ✓ 맞음 / 정정 피드백이 누적되어 ontology
            자동 학습.
            <br />
            정정율 낮을수록 model 정확. 챕터 분포 = 사용 패턴.
          </div>
        </div>
      </div>
    </div>
  );
}
